// libs
import React, { memo } from 'react';
import get from 'lodash/get';

// styles
import { Term, Icon, TextLabel } from './styles';

// constants
import { SEARCH_TYPE, SUGGESTION_TYPES } from 'shared/components/GlobalSearch/constants';
import { ROLES } from 'shared/constants';

// defs
import { ISuggestion } from 'shared/store/globalSearch/types';

// helpers
import {
  generateTrackingURLForSearch,
  addToRecentSearch,
  trackSearchCall,
} from 'shared/components/GlobalSearch/helpers';
import {
  resolveUrlFromCatalogTag,
  getCatalogTagStoreId,
  getStoreUrl,
} from 'shared/helpers/storeUrl';

// components
import ElementWrapper from 'shared/components/GlobalSearch/components/KeyboardWrapper/ElementWrapper';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useStoreId } from 'shared/store/app/hooks';

interface ISingleTerm {
  searchInfo: ISuggestion;
  searchType: string;
  icon: React.ReactNode;
  active: boolean;
  value: number;
  maxRecentSearches: number;
  onCategorySwitch?: (storeToRedirect: string, redirectUrl: string, productName: string) => void;
}

function SingleTerm({
  searchInfo,
  searchType,
  icon,
  active,
  value,
  maxRecentSearches,
  onCategorySwitch,
}: ISingleTerm) {
  // @ts-ignore: any since store can't be hardcoded as enums
  const storeSwitchBanner = useRemoteConfig('storeSwitchBanner');
  const currentStoreId = useStoreId();
  const title = get(searchInfo, 'suggestionWord', '');
  const subTitle = get(searchInfo, 'suggestionSubtitle', '');

  const term = subTitle !== '' ? `${title} ${subTitle}` : title;

  const onClick = () => {
    const resolvedUrl = resolveUrlFromCatalogTag(searchInfo);
    addToRecentSearch(
      {
        suggestionWord: searchInfo.suggestionWord,
        suggestionSubtitle: subTitle,
        type: searchInfo.type,
        catalog_tag: searchInfo.catalog_tag,
        url: resolvedUrl,
      },
      maxRecentSearches
    );
    trackSearchCall({
      typedSearchTerm: subTitle,
      searchTerm: searchInfo.suggestionWord,
      searchType: SEARCH_TYPE.RECENT_SEARCH,
    });

    const { catalog_tag, type } = searchInfo;
    if (type === SUGGESTION_TYPES.CATEGORY || type === SUGGESTION_TYPES.BRAND) {
      const crossStoreId = getCatalogTagStoreId(catalog_tag, currentStoreId);
      if (crossStoreId) {
        // @ts-ignore: any since store can't be hardcoded as enums
        const storeConfig = storeSwitchBanner?.[crossStoreId];
        const storeSwitch =
          (type === SUGGESTION_TYPES.BRAND && storeConfig?.brandStoreSwitch?.enabled) ||
          (type === SUGGESTION_TYPES.CATEGORY && storeConfig?.categoryStoreSwitch?.enabled);
        if (storeSwitch && onCategorySwitch) {
          onCategorySwitch(crossStoreId, resolvedUrl, searchInfo.suggestionWord);
          return;
        }
        window.location.href = getStoreUrl(resolvedUrl);
        return;
      }
    }

    window.location.href = generateTrackingURLForSearch({
      url: resolvedUrl,
      searchType: searchType,
      internalSearchTerm: searchInfo.suggestionWord,
      suggestionSubtitle: subTitle,
    });
  };

  if (!term) {
    return null;
  }

  return (
    <ElementWrapper indexValue={value} onClick={onClick}>
      <Term
        onClick={onClick}
        role={ROLES.BUTTON}
        active={active}
        aria-label={term}
        data-test="Suggestion-term"
        data-term={term}
      >
        <Icon>{icon}</Icon>
        <TextLabel>{term}</TextLabel>
      </Term>
    </ElementWrapper>
  );
}

export default memo(SingleTerm);
